import { z } from "zod";
import { ConversationMetaSchema } from "./slide";

/**
 * Real-time Pronunciation and Grammar Coaching Schemas
 * Used for WebSocket communication between mobile app and API server
 * Integrates with Azure Speech Pronunciation Assessment and Azure OpenAI Realtime API
 */

// ============================================================================
// WebSocket Message Types
// ============================================================================

/**
 * Message types for bidirectional WebSocket communication
 */
export const WebSocketMessageTypeEnum = z.enum([
  // Client -> Server
  "session_start", // Initialize coaching session with reference text and vocabulary
  "audio_chunk", // Stream audio data
  "session_end", // End coaching session
  "pause", // Pause assessment temporarily
  "resume", // Resume assessment
  "client_timing", // Client-measured latency telemetry (Phase 0 instrumentation)

  // Server -> Client
  "session_ready", // Session initialized successfully
  "speech_processing", // Azure recognized speech, AI response incoming
  "partial_recognition", // Interim speech recognition result (real-time)
  "pronunciation_feedback", // Real-time pronunciation scores
  "conversation_response", // AI conversational nudge/response
  "tts_audio_ready", // Server-pushed TTS audio for an AI response part
  "grammar_feedback", // Grammar correction feedback
  "grammar_assessment_ready", // Async end-of-session grammar assessment scored (carries the public result)
  "grammar_assessment_unscorable", // Async grammar assessment retained but not scorable (public status only)
  "grammar_assessment_failed", // Async grammar assessment permanently failed (public status only)
  "error", // Error message
]);

export type WebSocketMessageType = z.infer<typeof WebSocketMessageTypeEnum>;

// ============================================================================
// Session Management
// ============================================================================

/**
 * Vocabulary list for sandbox constraint
 * Words that the student has been taught and is allowed to use
 */
export const VocabularyItemSchema = z.object({
  word: z.string(),
  phonetic: z.string().optional(), // IPA representation
  difficulty: z.enum(["easy", "medium", "hard"]).optional(),
});

export type VocabularyItem = z.infer<typeof VocabularyItemSchema>;

export const ConversationHistorySchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string(),
});

export const SessionModeEnum = z.enum([
  "speech_to_text",
  "word_assessment",
  "speech_to_text_for_ai",
  "record_only",
  // Phonics scoring on Azure, streaming like word_assessment.
  "phonics_assessment",
]);
export type SessionMode = z.infer<typeof SessionModeEnum>;

/**
 * Client sends this to start a coaching session
 */
export const SessionStartMessageSchema = z.object({
  type: z.literal("session_start"),
  payload: z.object({
    mode: SessionModeEnum,
    sessionId: z.string().min(1), // Unique session identifier
    lessonId: z.string().min(1), // Lesson ID for cumulative learning
    slideId: z.string().optional(), // Slide ID (for telemetry tagging)
    // One slide play, minted by the renderer. Sent so the server can hold each scored take for
    // recordSlideScore to collect; without it the take is scored and shown but never persisted.
    attemptKey: z.string().optional(),
    // Which piece of the slide is being recorded — a passage paragraph id. Empty for slides
    // recorded as a whole.
    partId: z.string().optional(),
    knowledgeArea: z.string().optional(), // e.g. "form" (for telemetry tagging)
    referenceText: z.string(), // Text the student should say
    // Phoneme record behind a phonics decode slide. Sent because the same letter can be
    // two sounds (short-vowel "a" vs schwa "a") that referenceText alone cannot separate.
    phonemeId: z.string().nullish(),
    language: z.string().default("en-US"), // e.g., "en-US", "zh-TW"
    studentId: z.string(), // For tracking and personalization
    // Active learning-session id (queued practice) vs picker practice; carried so terminal
    // grammar-assessment handling can classify how the practiced slide was reached. Null/absent = picker.
    learningSessionId: z.string().nullish(),
    currentQuestion: z.string().optional(), // Current question from the slide for context
    includeDetailedScoring: z.boolean().optional(),
    history: z.array(ConversationHistorySchema).optional(),
    historyOffset: z.number().int().nonnegative().optional(), // Number of pre-loaded historical messages; excluded from turn-limit counting
    limitation: z
      .object({
        grammarLimit: z.string().nullable().optional(),
        grammarFocus: z.string().nullable().optional(),
        wordLimit: z.string().nullable().optional(),
        wordFocus: z.string().nullable().optional(),
        // Server-owned per-word categories the client shipped with the slide, so
        // the engine phrases category-aware vocab questions.
        conversationMeta: ConversationMetaSchema.nullable().optional(),
      })
      .optional(), // Slide grammar/vocabulary constraints for AI coaching
    voice: z.string().optional(), // Preferred TTS voice for server-side pre-fire of AI response audio
    // Phase 3: image-bearing focus words the mobile resolved from same-lesson word slides. The server
    // cycles these as the conversation's "visible item" and drives the per-turn image. Mobile-built (it
    // already loads the lesson slides), so no extra server query.
    conversationVisuals: z
      .array(
        z.object({
          word: z.string(),
          imageUrl: z.string(),
          slideId: z.string(),
        })
      )
      .optional(),
  }),
});

export type SessionStartMessage = z.infer<typeof SessionStartMessageSchema>;

/**
 * Server confirms session is ready
 */
export const SessionReadyMessageSchema = z.object({
  type: z.literal("session_ready"),
  payload: z.object({
    sessionId: z.string().min(1),
    message: z.string(),
  }),
});

export type SessionReadyMessage = z.infer<typeof SessionReadyMessageSchema>;

export const SpeechProcessingMessageSchema = z.object({
  type: z.literal("speech_processing"),
  payload: z.object({
    sessionId: z.string().min(1),
    turnId: z.string().optional(), // Correlates client timing with this turn
  }),
});

export type SpeechProcessingMessage = z.infer<typeof SpeechProcessingMessageSchema>;

export const PartialRecognitionMessageSchema = z.object({
  type: z.literal("partial_recognition"),
  payload: z.object({
    sessionId: z.string().min(1),
    text: z.string(),
  }),
});

export type PartialRecognitionMessage = z.infer<typeof PartialRecognitionMessageSchema>;

// ============================================================================
// Audio Streaming
// ============================================================================

/**
 * Audio chunk metadata and data
 * Audio data is sent as Base64-encoded PCM string
 */
export const AudioChunkMessageSchema = z.object({
  type: z.literal("audio_chunk"),
  payload: z.object({
    sessionId: z.string().min(1),
    sequenceNumber: z.number().int().nonnegative(), // For ordering chunks
    timestamp: z.number(), // Client timestamp in ms
    audioData: z.string().optional(), // Base64-encoded PCM audio data
    sampleRate: z.number().int().default(16000), // Audio sample rate in Hz
    channels: z.number().int().default(1), // Mono = 1, Stereo = 2
    encoding: z.literal("pcm_s16le").default("pcm_s16le"), // WAV PCM 16-bit little-endian
  }),
});

export type AudioChunkMessage = z.infer<typeof AudioChunkMessageSchema>;

/**
 * Session update
 * Update currentQuestion and history after get ai response
 */
export const UpdateSessionSchema = z.object({
  type: z.literal("update_session"),
  payload: z.object({
    sessionId: z.string().min(1),
    currentQuestion: z.string(),
    currentConversation: z.array(ConversationHistorySchema),
  }),
});

export type UpdateSession = z.infer<typeof UpdateSessionSchema>;

// ============================================================================
// Azure Speech Pronunciation Assessment Response
// ============================================================================

/**
 * Phoneme-level assessment from Azure Speech
 */
export const PhonemeAssessmentSchema = z.object({
  phoneme: z.string(),
  accuracyScore: z.number().min(0).max(100),
  offset: z.number().optional(), // Time offset in ticks
  duration: z.number().optional(), // Duration in ticks
  // Azure's NBestPhonemes: what it thinks was actually said here, best first.
  candidates: z.array(z.object({ phoneme: z.string(), score: z.number() })).optional(),
});

export type PhonemeAssessment = z.infer<typeof PhonemeAssessmentSchema>;

/**
 * Syllable-level assessment from Azure Speech
 */
export const SyllableAssessmentSchema = z.object({
  syllable: z.string(),
  grapheme: z.string(),
  accuracyScore: z.number().min(0).max(100),
  offset: z.number().optional(),
  duration: z.number().optional(),
});

export type SyllableAssessment = z.infer<typeof SyllableAssessmentSchema>;

/**
 * Word-level assessment from Azure Speech
 */
export const WordAssessmentSchema = z.object({
  word: z.string(),
  accuracyScore: z.number().min(0).max(100),
  pronScore: z.number().min(0).max(100),
  errorType: z.enum(["None", "Omission", "Insertion", "Mispronunciation"]),
  offset: z.number(), // Time offset in ticks (100 nanoseconds)
  duration: z.number(), // Duration in ticks
  phonemes: z.array(PhonemeAssessmentSchema).optional(),
  syllables: z.array(SyllableAssessmentSchema).optional(),
});

export type WordAssessment = z.infer<typeof WordAssessmentSchema>;

/**
 * Overall pronunciation assessment scores
 */
export const PronunciationScoresSchema = z.object({
  accuracyScore: z.number().min(0).max(100),
  fluencyScore: z.number().min(0).max(100),
  completenessScore: z.number().min(0).max(100),
  pronScore: z.number().min(0).max(100), // Overall pronunciation score
});

export type PronunciationScores = z.infer<typeof PronunciationScoresSchema>;

/**
 * Real-time pronunciation feedback sent to client
 * Server sends this as streaming feedback while student speaks
 */
export const PronunciationFeedbackMessageSchema = z.object({
  type: z.literal("pronunciation_feedback"),
  payload: z.object({
    sessionId: z.string().min(1),
    turnId: z.string().optional(), // Correlates client timing with this turn
    timestamp: z.number(), // Server timestamp
    recognizedText: z.string(), // What Azure recognized
    scores: PronunciationScoresSchema,
    words: z.array(WordAssessmentSchema),
    // Backend-processed final score (word_assessment mode only)
    // Includes accuracy override + phoneme gate + errorType cross-validation
    finalScore: z.number().min(0).max(100).optional(),
    audioUrl: z.string().url().optional(), // S3 URL of the recognized audio segment
    // The same clip's storage key. Signed URLs expire, so records keep the key and re-sign from it.
    recordingId: z.string().optional(),
    // Corrective hint for phonics_assessment (e.g. "Sounds like 'puh' — try 'buh'!")
    hint: z.string().optional(),
  }),
});

export const RecordingReadyMessageSchema = z.object({
  type: z.literal("recording_ready"),
  payload: z.object({
    sessionId: z.string().min(1),
    recordingUrl: z.string(),
  }),
});

export type RecordingReadyMessage = z.infer<typeof RecordingReadyMessageSchema>;

export type PronunciationFeedbackMessage = z.infer<typeof PronunciationFeedbackMessageSchema>;

// ============================================================================
// Azure OpenAI Realtime API Response
// ============================================================================

/**
 * Conversational response from Azure OpenAI Realtime API
 * AI provides grammar correction and gentle guidance back to taught vocabulary
 */
export const ConversationResponseMessageSchema = z.object({
  type: z.literal("conversation_response"),
  payload: z.object({
    sessionId: z.string().min(1),
    turnId: z.string().optional(), // Correlates client timing with this turn
    timestamp: z.number(),
    responseType: z.enum([
      "encouragement", // Positive reinforcement
      "correction", // Grammar or vocabulary correction
      "nudge", // Gentle guidance back to taught vocabulary
      "clarification", // Ask for clarification
    ]),
    message: z.string(), // Text message from AI
    learningAssessment: z
      .object({
        lexicalAccuracy: z.number(),
        grammaticalAccuracy: z.number(),
        semanticComprehension: z.number(),
        communicative: z.number(),
        errorDensity: z.number(),
      })
      .nullable()
      .optional(),
    audioUrl: z.string().url().optional(), // Optional audio response URL
    suggestedPhrase: z.string().optional(), // Suggested phrase to say
    shouldTerminate: z.boolean().nullish(),
    overallSessionScore: z.number().nullish(),
    // Phase 3: the item the teacher is "pointing at" this turn. Mobile resolves the IMAGE by slideId
    // (not word — dup/normalization could pick the wrong one) from its local conversationVisuals.
    visibleSlideId: z.string().nullish(),
    visibleWord: z.string().nullish(),
    // v4: the per-bubble image timeline (slideId per `|||` bubble, indexed by partIndex; null = show
    // nothing for that bubble). Mirrors the `tts_audio_ready.visibleSlideId` fast path so a bubble whose
    // audio synthesis failed still carries its visual cue — the client backfills any missing per-bubble
    // image from here. Additive: older clients ignore it.
    bubbleSlideIds: z.array(z.string().nullable()).nullish(),
    // Grammar-assessment: the id of the async assessment created for this (terminal) turn, so the client
    // can watch its scoring lifecycle (poll `grammarAssessment(id)` and match the `grammar_assessment_*`
    // events). Present only on the terminal turn that created an assessment. Additive: older clients ignore it.
    assessmentId: z.string().nullish(),
    // The per-turn ATTEMPT generation this response belongs to. A content-filter retry / stall-fallback
    // re-streams parts from index 0 under the SAME `timestamp`; this monotonically-increasing counter lets
    // the client discard a superseded attempt's audio/text and play only the final one. Additive: older
    // clients treat it as generation 0.
    generation: z.number().int().nonnegative().optional(),
  }),
});

export type ConversationResponseMessage = z.infer<typeof ConversationResponseMessageSchema>;

// ============================================================================
// Async Grammar Assessment Events (server -> client)
// ============================================================================

/**
 * Public, student-facing grammar-assessment result (0-100 INTEGER scores). Distinct from the
 * legacy 0-1 `learningAssessment` wire shape. `wordChoiceScore`/`grammarScore`/`pronunciationScore`
 * are nullable (display as an em dash when null); a dimension with no evidence is dropped from the
 * overall aggregate rather than scored as zero.
 */
export const GrammarAssessmentResultSchema = z.object({
  overallScore: z.number().int(),
  wordChoiceScore: z.number().int().nullable(),
  grammarScore: z.number().int().nullable(),
  understandingScore: z.number().int(),
  communicationScore: z.number().int(),
  majorErrorCount: z.number().int(),
  pronunciationScore: z.number().int().nullable(),
  realLifeSummary: z.string(),
});

export type GrammarAssessmentResultPayload = z.infer<typeof GrammarAssessmentResultSchema>;

/**
 * Best-effort live delivery of a completed async assessment. These are flat frames (no `payload`
 * wrapper) pushed directly by the runner's event publisher; persistence remains authoritative, so a
 * client that missed the frame recovers the same result via the `grammarAssessment(id)` query.
 */
export const GrammarAssessmentReadyMessageSchema = z.object({
  type: z.literal("grammar_assessment_ready"),
  assessmentId: z.string(),
  sessionId: z.string(),
  result: GrammarAssessmentResultSchema,
});

export type GrammarAssessmentReadyMessage = z.infer<typeof GrammarAssessmentReadyMessageSchema>;

/** Retained but not scorable — carries public status + id only; raw reasons stay server-only. */
export const GrammarAssessmentUnscorableMessageSchema = z.object({
  type: z.literal("grammar_assessment_unscorable"),
  assessmentId: z.string(),
  sessionId: z.string(),
});

export type GrammarAssessmentUnscorableMessage = z.infer<
  typeof GrammarAssessmentUnscorableMessageSchema
>;

/** Permanently failed after the retry budget — public status + id only; raw errors stay server-only. */
export const GrammarAssessmentFailedMessageSchema = z.object({
  type: z.literal("grammar_assessment_failed"),
  assessmentId: z.string(),
  sessionId: z.string(),
});

export type GrammarAssessmentFailedMessage = z.infer<typeof GrammarAssessmentFailedMessageSchema>;

/**
 * Server-pushed TTS audio for an AI response part
 * Sent after `conversation_response`, one message per `|||`-separated segment,
 * so the client can play audio without an extra round-trip to synthesizeSpeech.
 */
export const TtsAudioReadyMessageSchema = z.object({
  type: z.literal("tts_audio_ready"),
  payload: z.object({
    sessionId: z.string().min(1),
    turnId: z.string().optional(), // Correlates client timing with this turn
    conversationTimestamp: z.number(), // Matches ConversationResponseMessage.payload.timestamp
    partIndex: z.number().int().nonnegative(),
    partText: z.string(),
    audioBase64: z.string().optional(), // Base64-encoded MP3 bytes
    src: z.string().optional(), // S3 URL once background upload completes (may be empty if upload not done)
    voice: z.string().optional(), // Voice used to synthesize (informational; client matches by text)
    // Corrected word(s) for this bubble — the client renders them RED in the speech bubble. Omitted
    // when the bubble has no correction. Additive: older clients ignore it.
    emphasis: z.array(z.string()).optional(),
    // Round 8: the image to show WHILE this bubble plays (per-bubble timeline) — the client switches the
    // picture per bubble so it stays in sync with the spoken line. Null = show nothing; omitted = leave
    // the per-turn image. Additive: older clients ignore it.
    visibleSlideId: z.string().nullish(),
    // The per-turn ATTEMPT generation this audio belongs to (matches ConversationResponseMessage.generation).
    // A retry/fallback re-streams part 0 under the same conversationTimestamp; the client discards audio
    // from a superseded generation. Additive: older clients treat it as generation 0.
    generation: z.number().int().nonnegative().optional(),
  }),
});

export type TtsAudioReadyMessage = z.infer<typeof TtsAudioReadyMessageSchema>;

/**
 * Grammar feedback from AI
 */
export const GrammarFeedbackMessageSchema = z.object({
  type: z.literal("grammar_feedback"),
  payload: z.object({
    sessionId: z.string().min(1),
    timestamp: z.number(),
    originalText: z.string(),
    correctedText: z.string().optional(),
    issues: z.array(
      z.object({
        type: z.enum(["grammar", "vocabulary", "structure"]),
        message: z.string(),
        severity: z.enum(["error", "warning", "suggestion"]),
      })
    ),
  }),
});

export type GrammarFeedbackMessage = z.infer<typeof GrammarFeedbackMessageSchema>;

/**
 * Grammar response from AI
 */
export const GetGrammarAIResponseSchema = z.object({
  message: z.string(),
  scores: z
    .object({
      lexicalAccuracy: z.number(),
      grammaticalAccuracy: z.number(),
      semanticComprehension: z.number(),
      communicative: z.number(),
      errorDensity: z.number(),
    })
    .nullish(),
  shouldTerminate: z.boolean().nullish(),
  overallSessionScore: z.number().nullish(),
});

export type GetGrammarAIResponseType = z.infer<typeof GetGrammarAIResponseSchema>;

/**
 * JSON shape examples for prompt injection.
 * Kept in sync with GetGrammarAIResponseSchema so the LLM and the validator
 * agree on a single source of truth.
 */
export const grammarResponseJsonExample = {
  detailed: `{
  "message": "Your response|||Split with triple pipes",
  "scores": {
    "lexicalAccuracy": 0.85,
    "grammaticalAccuracy": 0.90,
    "semanticComprehension": 0.80,
    "communicative": 0.88,
    "errorDensity": 2.5
  },
  "shouldTerminate": false,
  "overallSessionScore": 75
}`,
  simple: `{
  "message": "Your response|||Split with triple pipes",
  "shouldTerminate": false,
  "overallSessionScore": 75
}`,
} as const;

// ============================================================================
// Control Messages
// ============================================================================

export const PauseMessageSchema = z.object({
  type: z.literal("pause"),
  payload: z.object({
    sessionId: z.string().min(1),
  }),
});

export type PauseMessage = z.infer<typeof PauseMessageSchema>;

export const ResumeMessageSchema = z.object({
  type: z.literal("resume"),
  payload: z.object({
    sessionId: z.string().min(1),
  }),
});

export type ResumeMessage = z.infer<typeof ResumeMessageSchema>;

export const SessionEndMessageSchema = z.object({
  type: z.literal("session_end"),
  payload: z.object({
    sessionId: z.string().min(1),
  }),
});

export type SessionEndMessage = z.infer<typeof SessionEndMessageSchema>;

// ============================================================================
// Error Messages
// ============================================================================

export const ErrorMessageSchema = z.object({
  type: z.literal("error"),
  payload: z.object({
    sessionId: z.string().min(1).optional(),
    code: z.string(), // Error code for client handling
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

export type ErrorMessage = z.infer<typeof ErrorMessageSchema>;

/**
 * Client-measured latency stages (single client clock). The server validates
 * `stage` against this list before persisting (Phase 0 instrumentation).
 * - `ttf`: client speech-end -> first teacher bubble playback started
 * - `synth_or_cache_load`: time to obtain the first bubble's audio (synth or cache)
 * - `response_received`: client speech-end -> conversation_response arrived
 */
export const ClientTimingStageEnum = z.enum(["ttf", "synth_or_cache_load", "response_received"]);
export type ClientTimingStage = z.infer<typeof ClientTimingStageEnum>;

/**
 * Client -> Server latency telemetry. One frame per measured stage, with the
 * duration computed entirely on the client clock (avoids client/server skew).
 */
export const ClientTimingMessageSchema = z.object({
  type: z.literal("client_timing"),
  payload: z.object({
    sessionId: z.string().min(1),
    turnId: z.string().min(1),
    stage: ClientTimingStageEnum,
    durationMs: z.number().nonnegative(),
  }),
});

export type ClientTimingMessage = z.infer<typeof ClientTimingMessageSchema>;

// ============================================================================
// Raw Azure Pronunciation Assessment Result Schemas
// These represent the structure of the JSON received directly from Azure PA service
// and stored in our database.
// ============================================================================

export const AzureSyllablePronunciationAssessmentSchema = z.object({
  AccuracyScore: z.number(),
});

export const AzureSyllableSchema = z.object({
  Syllable: z.string(),
  Grapheme: z.string().optional(),
  Duration: z.number().optional(),
  offset: z.number().optional(),
  PronunciationAssessment: AzureSyllablePronunciationAssessmentSchema,
});

export const AzureWordPronunciationAssessmentSchema = z.object({
  AccuracyScore: z.number(),
  ErrorType: z.string().optional(),
});

export const AzurePhonemeSchema = z.object({
  Phoneme: z.string(),
  PronunciationAssessment: z.object({ AccuracyScore: z.number() }),
});

export const AzureWordSchema = z.object({
  Word: z.string(),
  Offset: z.number().optional(),
  Duration: z.number().optional(),
  PronunciationAssessment: AzureWordPronunciationAssessmentSchema.optional(),
  Syllables: z.array(AzureSyllableSchema).optional(),
  Phonemes: z.array(AzurePhonemeSchema).optional(),
});

export const AzureOverallPronunciationAssessmentSchema = z.object({
  AccuracyScore: z.number(),
  FluencyScore: z.number(),
  CompletenessScore: z.number(),
  PronScore: z.number(),
});

export const AzureNBestSchema = z.object({
  Confidence: z.number().optional(),
  Lexical: z.string().optional(),
  ITN: z.string().optional(),
  MaskedITN: z.string().optional(),
  Display: z.string().optional(),
  PronunciationAssessment: AzureOverallPronunciationAssessmentSchema.optional(),
  Words: z.array(AzureWordSchema).optional(),
});

export const AzurePronunciationResultSchema = z.object({
  RecognitionStatus: z.string().optional(),
  Offset: z.number().optional(),
  Duration: z.number().optional(),
  NBest: z.array(AzureNBestSchema).optional(),
});

export type AzurePronunciationResult = z.infer<typeof AzurePronunciationResultSchema>;

// ============================================================================
// Union Type for All WebSocket Messages
// ============================================================================

/**
 * Discriminated union of all WebSocket message types
 * Use this for type-safe message parsing
 */
export const WebSocketMessageSchema = z.discriminatedUnion("type", [
  // Client -> Server
  SessionStartMessageSchema,
  AudioChunkMessageSchema,
  PauseMessageSchema,
  ResumeMessageSchema,
  SessionEndMessageSchema,
  UpdateSessionSchema,
  ClientTimingMessageSchema,

  // Server -> Client
  SessionReadyMessageSchema,
  SpeechProcessingMessageSchema,
  PartialRecognitionMessageSchema,
  PronunciationFeedbackMessageSchema,
  ConversationResponseMessageSchema,
  TtsAudioReadyMessageSchema,
  GrammarFeedbackMessageSchema,
  GrammarAssessmentReadyMessageSchema,
  GrammarAssessmentUnscorableMessageSchema,
  GrammarAssessmentFailedMessageSchema,
  RecordingReadyMessageSchema,
  ErrorMessageSchema,
]);

export type WebSocketMessage = z.infer<typeof WebSocketMessageSchema>;

// ============================================================================
// Color-coded Feedback Thresholds
// ============================================================================

/**
 * Thresholds for color-coded UI feedback
 * Green: Good, Yellow: Needs improvement, Red: Poor
 */
export const feedbackThresholds = {
  excellent: 90, // >= 90: Green
  good: 70, // >= 70: Yellow-Green
  needsWork: 50, // >= 50: Yellow
  poor: 0, // < 50: Red
} as const;

/**
 * Helper function to get feedback color based on score
 */
export function getFeedbackColor(score: number): "green" | "yellow" | "red" {
  if (score >= feedbackThresholds.excellent) return "green";
  if (score >= feedbackThresholds.needsWork) return "yellow";
  return "red";
}
